<template>
  <div class="cnc-checkbox" :class="size === 'medium' ? 'medium' : 'small' ">
    <el-checkbox v-model="val" v-bind="CheckBoxProps" :class="{ 'is-read-only': isReadOnly, 'is-error' : isError }" @change="val => $emit('change', val)" >
      <div class="fragment-name">
        <div class="txt-checkbox-name">
          <div v-if="name && name.length">{{ name }}</div>
          <slot v-else class="txt-checkbox-name" />
        </div>
        <div v-if="rightIcon">
          <SvgIcon v-if="rightIcon.type === 'svg'" :name="rightIcon.name"/>
          <Icon v-else :name="rightIcon.name"/>
        </div>
      </div>
      <div v-if="desc" class="txt-checkbox-desc" @click="$event => { $event.preventDefault() }">{{ desc }}</div>
      <div v-if="isError" class="txt-checkbox-error" @click="$event => { $event.preventDefault() }">{{ errMessage }}</div>
    </el-checkbox>
  </div>
</template>

<script>
export default {
  components: {
    Icon: () => import('./Icon.vue'),
  },
  props: {
    value: { type: Boolean },
    name: { type: String },
    label: { type: [String, Number] },
    disabled: { type: Boolean },
    size: { type: String, default: 'medium' },
    desc: { type: String },
    isReadOnly: { type: Boolean },
    isError: { type: Boolean },
    rightIcon: { type: Object },
    errMessage: { type: String },
    isIndeterminate: { type: Boolean }
  },
  data () {
    return {
      val: this.value
    }
  },
  computed: {
    CheckBoxProps () {
      return {
        disabled : this.disabled || this.isReadOnly,
        label: this.label || null,
        indeterminate: this.isIndeterminate
      }
    }
  },
  watch: {
    'value' (val, oldVal) {
        this.val = val
      },
    'val' (val) {
      this.$emit('update:value', val)
    }
  }
}
</script>

<style lang="scss">
.cnc-checkbox {
  .el-checkbox {
    .fragment-name {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;

      i {
        color: $color-cool-grey-500;
      }
    }
    
    .el-checkbox__input {
      vertical-align: top;
      padding-top: 2px;

      .el-checkbox__inner {
        &:hover {
          border-color: $color-cool-grey-500;
        }

        &:focus-visible {
          outline-offset: $space-4xs;
          outline: $space-4xs solid $color-primary-200;
        }
      }

      .el-checkbox__name {
        font-size: $font-size-md;
        color: $color-cool-grey-700;
      }

      .el-checkbox__inner::after {
        border: 2px solid #FFF;
        border-left: 0;
        border-top: 0;
      }
    }

    .is-checked {
      .el-checkbox__inner {
        &:hover {
          border-color: $color-primary-700 !important;
          background-color: $color-primary-700 !important;
        }
      }
    }

    .is-focus {
      .el-checkbox__inner{
        outline-offset: $space-4xs;
        outline: $space-4xs solid $color-primary-200;
      }
    }

    .txt-checkbox-name {
      white-space: pre-wrap;
      word-wrap: break-word !important;
    }

    .txt-checkbox-desc {
      color: $color-cool-grey-500;
      font-size: $font-size-sm;
      cursor: default;
      white-space: pre-wrap;
      word-wrap: break-word;
    }

    .txt-checkbox-error {
      color: $color-semanticRed-500;
      font-size: $font-size-sm;
      cursor: default;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
  }

  .is-disabled {
    .el-checkbox__name {
      color: $color-cool-grey-300;
    }

    .el-checkbox__inner {
      border-color: $color-cool-grey-300;
      background-color: $color-cool-grey-100;

      &:hover {
        border-color: $color-cool-grey-300 !important;
        background-color: $color-cool-grey-100 !important;
      }

      &:focus-visible {
        border-color: $color-cool-grey-300;
        background-color: $color-cool-grey-100;
      }
    }

    .txt-checkbox-desc, .txt-checkbox-name{
      color: $color-cool-grey-300;
    }


    .is-checked {
      .el-checkbox__inner {
        border-color: $color-cool-grey-300 !important;
        background-color: $color-cool-grey-300 !important;

        &:hover {
          border-color: $color-cool-grey-300 !important;
          background-color: $color-cool-grey-300 !important;
        }
      }
    }

    .is-indeterminate {
      .el-checkbox__inner {
        border-color: $color-cool-grey-300;
        background-color: $color-cool-grey-300;
      }
      .el-checkbox__inner::before {
        border: 2px solid $color-white;
      }
    }
  }

  .is-read-only {
    .txt-checkbox-name {
      color: $color-cool-grey-700;;
    }

    .txt-checkbox-desc {
      color: $color-cool-grey-500;
    }

    .is-checked { 
      .el-checkbox__inner {
        border-color: $color-primary-200 !important;
        background-color: $color-primary-200 !important;

        &:hover {
          border-color: $color-primary-200 !important;
          background-color: $color-primary-200 !important;
        }
      }
    }
  }

  .is-error {
    .el-checkbox__inner {
      border-color: $color-semanticRed-500 !important;

      &:hover {
        border-color: $color-semanticRed-700 !important;
      }

      &:focus-visible {
        outline-offset: 2px;
        outline: 2px solid $color-semanticRed-200 !important;
      }

      .el-checkbox__inner::after {
        border: 2px solid $color-semanticRed-500 !important;
      }
    }

    .is-checked {
      .el-checkbox__inner {
        border-color: $color-semanticRed-500 !important;
        background-color: $color-white !important;

        &:hover {
          border-color: $color-semanticRed-700 !important;
          background-color: $color-white !important;
        }
      }

      .el-checkbox__inner::after {
        border: 2px solid $color-semanticRed-500 !important;
        border-left: 0 !important;
        border-top: 0 !important;
      }

      .el-checkbox__inner:hover::after {
        border: 2px solid $color-semanticRed-700 !important;
        border-left: 0 !important;
        border-top: 0 !important;
      }
    }

    .is-indeterminate {
      .el-checkbox__inner {
        background-color: transparent !important;
      }
      .el-checkbox__inner::before {
        border: 2px solid $color-semanticRed-500 !important;
      }
    }
  }

  &.small {
    .el-checkbox {
      .txt-checkbox-name {
        font: $font-style-body-md-reg;
      }

      .txt-checkbox-desc {
        font: $font-style-body-sm-reg;
      }

      .txt-checkbox-error {
        font: $font-style-body-sm-reg;
      }

      .fragment-name {
        i {
          font-size: $font-size-md;
        }
      }

      .el-checkbox__input {
        .el-checkbox__inner {
          width: 16px;
          height: 16px;
          border-radius: $radius-3xs;
        }

        .el-checkbox__name {
          font-size: $font-size-md;
        }

        .el-checkbox__inner::after {
          border: 2px solid #FFF;
          border-left: 0;
          border-top: 0;
        }
      }

      .is-indeterminate .el-checkbox__inner::before {
        height: 4px;
        top: 5px;
      }
    }
  }

  &.medium {
    .el-checkbox {
      .txt-checkbox-name {
        font: $font-style-body-lg-reg;
      }

      .txt-checkbox-desc {
        font: $font-style-body-md-reg;
      }

      .txt-checkbox-error {
        font: $font-style-body-md-reg;
      }

      .fragment-name {
        i {
          font-size: 20px;
        }
      }

      .el-checkbox__input {
        .el-checkbox__inner {
          width: 20px;
          height: 20px;
          border-radius: $radius-2xs;
        }

        .el-checkbox__name {
          font-size: $font-size-md;
        }

        .el-checkbox__inner::after {
          border: 2px solid #FFF;
          border-left: 0;
          border-top: 0;
          height: 8px;
          left: 6px;
          top: 3px;
        }
      }

      .is-indeterminate .el-checkbox__inner::before {
        height: 4px;
        top: 6px;
      }
    }
  }
}
</style>